import React, { createContext, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface ProductionNewParams {
  productId?: string;
  quantity?: number;
  warehouseId?: string;
}

export interface ProductionNav {
  openList(): void;
  openNew(params?: ProductionNewParams): void;
  openOrder(id: string): void;
  openTree(productId?: string): void;
  openWorkshop(): void;
  /** Rota modunda URL'den, yönetici modunda durumdan okunur. */
  newParams: ProductionNewParams | null;
  /** Diğer modüllere link verilebilir mi? Yönetici modunda false. */
  canLinkOutside: boolean;
}

const ProductionNavContext = createContext<ProductionNav | null>(null);

export interface ProductionOrderNavigationState {
  message?: string;
  openComplete?: boolean;
}

let pendingOrderState: ProductionOrderNavigationState | null = null;

/** Sonraki openOrder çağrısına rota durumu ekler. Yeni kayıt snackbar ve tamamlama diyaloğu için. */
export function queueProductionOrderNavigation(state: ProductionOrderNavigationState) {
  pendingOrderState = state;
}

export const ProductionNavProvider: React.FC<{ value: ProductionNav; children: React.ReactNode }> = ({
  value,
  children,
}) => <ProductionNavContext.Provider value={value}>{children}</ProductionNavContext.Provider>;

function readNewParams(params: URLSearchParams): ProductionNewParams | null {
  const productId = params.get('product') || undefined;
  const warehouseId = params.get('warehouse') || undefined;
  const qtyRaw = params.get('qty');
  const parsed = qtyRaw == null || qtyRaw === '' ? Number.NaN : Number(qtyRaw);
  const quantity = Number.isFinite(parsed) ? parsed : undefined;
  if (!productId && quantity == null && !warehouseId) return null;
  return { productId, quantity, warehouseId };
}

/** /uretim/fisler, /uretim/yeni?product&qty&warehouse, /uretim/fis/:id, /uretim/agac?product= */
export function useRouteProductionNav(): ProductionNav {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const newParams = useMemo(() => readNewParams(params), [params]);

  return useMemo<ProductionNav>(() => ({
    openList: () => navigate('/uretim/fisler'),
    openNew: (next?: ProductionNewParams) => {
      const search = new URLSearchParams();
      if (next?.productId) search.set('product', next.productId);
      if (next?.quantity != null && Number.isFinite(next.quantity)) search.set('qty', String(next.quantity));
      if (next?.warehouseId) search.set('warehouse', next.warehouseId);
      const query = search.toString();
      navigate(query ? `/uretim/yeni?${query}` : '/uretim/yeni');
    },
    openOrder: (id: string) => {
      const state = pendingOrderState;
      pendingOrderState = null;
      navigate(`/uretim/fis/${id}`, {
        replace: location.pathname === '/uretim/yeni',
        state,
      });
    },
    openTree: (productId?: string) => {
      navigate(productId ? `/uretim/agac?product=${encodeURIComponent(productId)}` : '/uretim/agac');
    },
    openWorkshop: () => navigate('/uretim/atolye'),
    newParams,
    canLinkOutside: true,
  }), [navigate, newParams, location.pathname]);
}

/** Sağlayıcı yoksa rota uygulamasını döner. */
export function useProductionNav(): ProductionNav {
  const provided = useContext(ProductionNavContext);
  const route = useRouteProductionNav();
  return provided ?? route;
}
